import axios from "axios";
import { ACCESS_TOKEN_NAME } from "common/globals";

const VISITOR_KEY = "mathboard-visitor-id";
const VISITOR_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const PAGEVIEW_PATHS = ["/", "/practice", "/he/practice"] as const;

let lastTrackedKey = "";

export function normalizePageviewPath(path: string): (typeof PAGEVIEW_PATHS)[number] | null {
  const cut = path.split("?")[0].split("#")[0];
  const pathOnly = cut.length > 1 ? cut.replace(/\/+$/, "") : cut || "/";
  return (PAGEVIEW_PATHS as readonly string[]).includes(pathOnly)
    ? (pathOnly as (typeof PAGEVIEW_PATHS)[number])
    : null;
}

export function shouldSendPageview(
  path: string,
  doNotTrack: string | null | undefined,
): boolean {
  if (doNotTrack === "1") return false;
  return normalizePageviewPath(path) != null;
}

function visitorId(): string | null {
  try {
    const existing = localStorage.getItem(VISITOR_KEY);
    if (existing && VISITOR_ID.test(existing)) return existing;
    const next = crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, next);
    return next;
  } catch {
    return null;
  }
}

function referrerHost(): string | undefined {
  try {
    if (!document.referrer) return undefined;
    return new URL(document.referrer).hostname || undefined;
  } catch {
    return undefined;
  }
}

function accessToken(): string | undefined {
  const prefix = `${ACCESS_TOKEN_NAME}=`;
  const hit = document.cookie.split("; ").find((part) => part.startsWith(prefix));
  if (!hit) return undefined;
  try {
    const value = decodeURIComponent(hit.slice(prefix.length));
    if (!value || value === "null" || value === "undefined") return undefined;
    return value;
  } catch {
    return undefined;
  }
}

function languageFor(path: string): "en" | "he" {
  if (path === "/he/practice") return "he";
  if (path === "/practice") return "en";
  return document.documentElement.lang === "he" ? "he" : "en";
}

/** One POST per arrival at /, /practice, or /he/practice. Failures stay on the page. */
export function trackPageView(path: string): void {
  if (typeof window === "undefined") return;
  const doNotTrack = navigator.doNotTrack ?? (window as Window & { doNotTrack?: string }).doNotTrack;
  if (!shouldSendPageview(path, doNotTrack)) return;
  const page = normalizePageviewPath(path);
  if (!page) return;
  const key = `${page}${window.location.search}`;
  if (key === lastTrackedKey) return;
  const id = visitorId();
  if (!id) return;
  lastTrackedKey = key;

  const params = new URLSearchParams(window.location.search);
  const gclid = params.get("gclid") || undefined;
  const utmSource = params.get("utm_source") || undefined;
  const utmMedium = params.get("utm_medium") || undefined;
  const utmCampaign = params.get("utm_campaign") || undefined;
  const host = referrerHost();
  const token = accessToken();

  void axios
    .post(
      "/api/pageview",
      {
        path: page,
        language: languageFor(page),
        ...(host ? { referrerHost: host } : {}),
        ...(gclid ? { gclid } : {}),
        ...(utmSource ? { utmSource } : {}),
        ...(utmMedium ? { utmMedium } : {}),
        ...(utmCampaign ? { utmCampaign } : {}),
        visitorId: id,
      },
      token ? { headers: { authorization: token } } : undefined,
    )
    .catch(() => {});
}
